import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  deployPwaToken,
  ensurePwaWallet,
  fetchPwaWallet,
  fetchPwaAuthUser,
  type TelegramPwaWallet,
  type TelegramUser,
  type PwaAuthUser
} from '../../utils/telegramAuth';
import { STORAGE_BUCKETS, uploadBlobToStorage, uploadImageToStorage } from '../../utils/supabase';

const PageContainer = styled.div`
  width: 100%;
  min-height: calc(100vh - 80px);
  padding: 16px 16px 100px 16px;
  box-sizing: border-box;
  color: #ffffff;
`;

const PageTitle = styled.h1`
  font-size: 20px;
  margin: 0 0 12px 0;
  letter-spacing: 0.4px;
`;

const Card = styled.div`
  background: rgba(20, 20, 25, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HelperText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SectionHeaderRow = styled.button<{ $open?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 10px 12px;
  color: #ffffff;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  cursor: pointer;
`;

const SectionChevron = styled.span<{ $open?: boolean }>`
  font-size: 14px;
  transform: ${props => props.$open ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.2s ease;
`;

const ToggleRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const Label = styled.label`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(255, 255, 255, 0.7);
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: #ffffff;
  font-size: 13px;
  box-sizing: border-box;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  border: 1px dashed rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.02);
`;

const LogoPreview = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 420px;
  background: #0b0b0f;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 18px;
  cursor: pointer;
`;

const ModalActionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
`;

const ModalButton = styled.button<{ $secondary?: boolean }>`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: ${props => props.$secondary ? 'rgba(255, 255, 255, 0.05)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'};
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:active {
    transform: scale(0.98);
  }
`;

const ModalButtonDanger = styled(ModalButton)`
  background: rgba(248, 113, 113, 0.18);
  border-color: rgba(248, 113, 113, 0.6);
  color: #fecaca;
`;

const ResultCard = styled.div`
  margin-top: 12px;
  background: rgba(10, 10, 15, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ResultHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const ResultLogo = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ResultTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
`;

const ResultSubtitle = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const ResultRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ResultLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(255, 255, 255, 0.6);
`;

const ResultValueButton = styled.button`
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 13px;
  text-align: left;
  padding: 0;
  cursor: pointer;
  word-break: break-all;
`;

const ResultLink = styled.a`
  color: #10b981;
  text-decoration: none;
  font-size: 13px;
  word-break: break-all;
`;

const LogArea = styled.pre`
  background: #0a0a0a;
  padding: 12px;
  border-radius: 10px;
  font-family: monospace;
  font-size: 11px;
  color: #14f195;
  white-space: pre-wrap;
  border: 1px solid rgba(20, 241, 149, 0.2);
`;

const CreateHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CreateTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
`;

const CreateSubTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
`;

const CreateInfoText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
`;

const CreatePreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const CreatePreviewLogo = styled.img`
  width: 72px;
  height: 72px;
  border-radius: 18px;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const CreatePreviewName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  text-align: center;
`;

const CreatePreviewSymbol = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
`;

interface SentryCreatePWAProps {
  authLabel?: string;
}

const SentryCreatePWA: React.FC<SentryCreatePWAProps> = ({ authLabel = 'Telegram' }) => {
  const TOKENS_PER_SOL = 41_000_000;
  const [telegramUser, setTelegramUser] = useState<PwaAuthUser | TelegramUser | null>(null);
  const [wallet, setWallet] = useState<TelegramPwaWallet | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [website, setWebsite] = useState('');
  const [twitter, setTwitter] = useState('');
  const [telegram, setTelegram] = useState('');
  const [devBuyAmount, setDevBuyAmount] = useState('');
  const [bundleBuyEnabled, setBundleBuyEnabled] = useState(false);
  const [bundleWalletCount, setBundleWalletCount] = useState('5');
  const [massCallEnabled, setMassCallEnabled] = useState(false);
  const [displayUsername, setDisplayUsername] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [logs, setLogs] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const [showBundleFeeModal, setShowBundleFeeModal] = useState(false);
  const [pendingDevBuyAmount, setPendingDevBuyAmount] = useState<number | undefined>(undefined);
  const [bundleDetailsOpen, setBundleDetailsOpen] = useState(false);
  const [socialsOpen, setSocialsOpen] = useState(false);
  const [deployResult, setDeployResult] = useState<{
    mintAddress: string;
    poolAddress?: string | null;
    lockSignature?: string | null;
    devBuySignature?: string | null;
    bundleFeeSignature?: string | null;
    bundleBuyExecuted?: boolean;
    bundleBuyWalletCount?: number;
    bundleBuyResults?: Array<{ wallet: string; amountSol: number; tokenAmount: number; signature: string }>;
    bundleBuyTokenTotal?: number | null;
    massCallFeeSignature?: string | null;
    massCallEnabled?: boolean;
    massCallSentCount?: number;
    massCallFailedCount?: number;
    symbol: string;
    devBuyTokenAmount?: number | null;
    logoUrl?: string;
    name: string;
  } | null>(null);
  const BUNDLE_FEE_SOL = 1;
  const MASS_CALL_FEE_SOL = 1;
  const logoInputRef = useRef<HTMLInputElement>(null);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, message]);
  };

  const estimatedDevBuyTokens = useMemo(() => {
    const amount = Number(devBuyAmount);
    if (!Number.isFinite(amount) || amount <= 0) return null;
    return amount * TOKENS_PER_SOL;
  }, [devBuyAmount, TOKENS_PER_SOL]);


  const canSubmit = useMemo(() => {
    return !!telegramUser && !!wallet && !!name.trim() && !!symbol.trim() && !isDeploying;
  }, [telegramUser, wallet, name, symbol, isDeploying]);

  useEffect(() => {
    let cancelled = false;

    const loadUser = async () => {
      setLoadingUser(true);
      setError(null);
      try {
        const user = await fetchPwaAuthUser();
        if (cancelled) return;
        setTelegramUser(user);
        if (!user) {
          setWallet(null);
          return;
        }
        const existing = await fetchPwaWallet();
        if (cancelled) return;
        if (existing) {
          setWallet(existing);
        } else {
          const created = await ensurePwaWallet();
          if (!cancelled) {
            setWallet(created);
          }
        }
      } catch (loadError: any) {
        if (!cancelled) {
          setError(loadError?.message || 'Unable to load Telegram session.');
        }
      } finally {
        if (!cancelled) {
          setLoadingUser(false);
        }
      }
    };

    loadUser();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const normalizeUrl = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    return `https://${trimmed}`;
  };

  const buildFileBase = () => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  };

  const handleDeploy = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setLogs([]);
    setDeployResult(null);

    if (!telegramUser) {
      setError('Log in with Telegram to create a token.');
      return;
    }
    if (!wallet) {
      setError('Wallet not available. Please refresh and try again.');
      return;
    }
    if (!name.trim() || !symbol.trim()) {
      setError('Token name and symbol are required.');
      return;
    }
    if (!logoFile) {
      setError('Token logo is required.');
      return;
    }

    const parsedDevBuy = devBuyAmount.trim() ? Number(devBuyAmount) : undefined;
    if (parsedDevBuy !== undefined && (!Number.isFinite(parsedDevBuy) || parsedDevBuy <= 0)) {
      setError('Dev buy amount must be a positive number.');
      return;
    }

    setPendingDevBuyAmount(parsedDevBuy);
    if (bundleBuyEnabled || massCallEnabled) {
      setShowBundleFeeModal(true);
      return;
    }
    startDeployment(parsedDevBuy);
  };

  const copyValue = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      addLog('Copied to clipboard.');
    } catch {
      addLog('Copy failed.');
    }
  };

  const startDeployment = async (devBuyAmountSol?: number) => {
    setError(null);
    setLogs([]);
    setIsDeploying(true);
    setShowBundleFeeModal(false);
    try {
      const fileBase = buildFileBase();
      let logoUrl = '';

      if (!logoFile) {
        throw new Error('Token logo is required.');
      }
      addLog('Uploading logo...');
      const fileName = `${fileBase}-logo.png`;
      const uploadedUrl = await uploadImageToStorage(logoFile, STORAGE_BUCKETS.SOLANA_LOGOS, fileName);
      if (!uploadedUrl) {
        throw new Error('Logo upload failed.');
      }
      logoUrl = uploadedUrl;
      addLog('Logo uploaded.');

      const metadata: Record<string, any> = {
        name: name.trim(),
        symbol: symbol.trim().toUpperCase(),
        description: 'Created with Sentry PWA powered by Mavrk.',
        image: logoUrl || undefined,
        properties: logoUrl ? { files: [{ uri: logoUrl, type: logoFile?.type || 'image/png' }], category: 'image' } : undefined
      };

      const normalizedWebsite = normalizeUrl(website);
      const normalizedTwitter = normalizeUrl(twitter);
      const normalizedTelegram = normalizeUrl(telegram);
      if (normalizedWebsite) metadata.external_url = normalizedWebsite;
      if (normalizedTwitter) metadata.twitter = normalizedTwitter;
      if (normalizedTelegram) metadata.telegram = normalizedTelegram;

      addLog('Uploading metadata...');
      const metadataBlob = new Blob([JSON.stringify(metadata)], { type: 'application/json' });
      const metadataFileName = `${fileBase}.json`;
      const metadataUrl = await uploadBlobToStorage(metadataBlob, STORAGE_BUCKETS.SOLANA_LOGOS, metadataFileName);
      if (!metadataUrl) {
        throw new Error('Metadata upload failed.');
      }
      addLog('Metadata uploaded.');

      if (devBuyAmountSol !== undefined && (!Number.isFinite(devBuyAmountSol) || devBuyAmountSol <= 0)) {
        throw new Error('Dev buy amount must be a positive number.');
      }

      let bundleWalletCountValue: number | undefined;
      if (bundleBuyEnabled) {
        const count = Number(bundleWalletCount);
        if (!Number.isFinite(count) || count < 1 || count > 50) {
          throw new Error('Bundle wallet count must be between 1 and 50.');
        }
        bundleWalletCountValue = Math.floor(count);
      }

      addLog('Submitting deployment...');
      const response = await deployPwaToken({
        name: name.trim(),
        symbol: symbol.trim().toUpperCase(),
        metadataUrl,
        logoUrl: logoUrl || null,
        website: normalizedWebsite || undefined,
        twitter: normalizedTwitter || undefined,
        telegram: normalizedTelegram || undefined,
        devBuyAmountSol,
        bundleBuyEnabled,
        bundleWalletCount: bundleWalletCountValue,
        massCallEnabled,
        displayUsername
      });

      addLog('Token deployed.');
      setDeployResult({
        mintAddress: response.mintAddress,
        poolAddress: response.poolAddress,
        lockSignature: response.lockSignature,
        devBuySignature: response.devBuySignature ?? null,
        bundleFeeSignature: response.bundleFeeSignature ?? null,
        bundleBuyExecuted: response.bundleBuyExecuted ?? false,
        bundleBuyWalletCount: response.bundleBuyWalletCount ?? undefined,
        bundleBuyResults: response.bundleBuyResults ?? [],
        bundleBuyTokenTotal: response.bundleBuyTokenTotal ?? null,
        massCallFeeSignature: response.massCallFeeSignature ?? null,
        massCallEnabled: response.massCallEnabled ?? false,
        massCallSentCount: response.massCallSentCount ?? undefined,
        massCallFailedCount: response.massCallFailedCount ?? undefined,
        symbol: symbol.trim().toUpperCase(),
        devBuyTokenAmount: response.devBuyTokenAmount ?? null,
        logoUrl: logoUrl || '',
        name: name.trim()
      });
      setBundleDetailsOpen(false);
    } catch (deployError: any) {
      setError(deployError?.message || 'Deployment failed.');
      addLog(`Error: ${deployError?.message || deployError}`);
    } finally {
      setIsDeploying(false);
    }
  };

  return (
    <PageContainer>
      <PageTitle>Create</PageTitle>
      <Card>
        {loadingUser && <HelperText>Loading {authLabel} session…</HelperText>}
        {!loadingUser && !telegramUser && (
          <HelperText>Log in with {authLabel} to deploy a Sentry token.</HelperText>
        )}
        {!loadingUser && telegramUser && !wallet && (
          <HelperText>Preparing your Sentry wallet…</HelperText>
        )}
        {!loadingUser && telegramUser && wallet && (
          <>
            <CreateHeader>
              <CreateTitle>Create a new coin</CreateTitle>
              <CreateSubTitle>Coin info</CreateSubTitle>
              <CreateInfoText>Fill out the information for your new coin below</CreateInfoText>
            </CreateHeader>
            {(logoPreview || name.trim() || symbol.trim()) && (
              <CreatePreview>
                {logoPreview && (
                  <CreatePreviewLogo src={logoPreview} alt="Coin logo" />
                )}
                <CreatePreviewName>{name.trim() || 'COIN NAME'}</CreatePreviewName>
                <CreatePreviewSymbol>{symbol.trim().toUpperCase() || 'COIN SYMBOL'}</CreatePreviewSymbol>
              </CreatePreview>
            )}
            <Form onSubmit={handleDeploy}>
              <FormGroup>
                <Label>Coin Name</Label>
                <Input
                  type="text"
                  placeholder="My Token"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={isDeploying}
                />
              </FormGroup>
              <FormGroup>
                <Label>Coin Symbol</Label>
                <Input
                  type="text"
                  placeholder="TKN"
                  value={symbol}
                  onChange={(event) => setSymbol(event.target.value.toUpperCase())}
                  maxLength={10}
                  disabled={isDeploying}
                />
              </FormGroup>
              <FormGroup>
                <Label>Coin Logo</Label>
                <FileInput
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={isDeploying}
                />
                <FileInputLabel
                  onClick={(event) => {
                    if (!isDeploying) {
                      logoInputRef.current?.click();
                    }
                    event.preventDefault();
                  }}
                >
                  {logoPreview ? (
                    <>
                      <span>Logo ready</span>
                      <LogoPreview src={logoPreview} alt="Logo preview" />
                    </>
                  ) : (
                    <span>Tap to upload logo</span>
                  )}
                </FileInputLabel>
              </FormGroup>
              <FormGroup>
                <Label>Dev Buy (SOL)</Label>
                <Input
                  type="number"
                  placeholder="0.01"
                  value={devBuyAmount}
                  onChange={(event) => setDevBuyAmount(event.target.value)}
                  min="0"
                  step="0.000001"
                  disabled={isDeploying}
                />
                {estimatedDevBuyTokens !== null ? (
                  <HelperText>
                    Estimated: {estimatedDevBuyTokens.toLocaleString()} {symbol.trim().toUpperCase() || 'TOKENS'}
                  </HelperText>
                ) : (
                  <HelperText>
                    Estimate uses the launch price (~41M tokens per SOL).
                  </HelperText>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Bundle Buy</Label>
                <ToggleRow>
                  <input
                    type="checkbox"
                    checked={bundleBuyEnabled}
                    onChange={(event) => setBundleBuyEnabled(event.target.checked)}
                    disabled={isDeploying}
                  />
                  Enable bundle buy across multiple wallets
                </ToggleRow>
                {bundleBuyEnabled && (
                  <>
                    <Input
                      type="number"
                      placeholder="Wallet count (1-50)"
                      value={bundleWalletCount}
                      onChange={(event) => setBundleWalletCount(event.target.value)}
                      min="1"
                      max="50"
                      step="1"
                      disabled={isDeploying}
                    />
                    <HelperText>
                      Bundle wallets must be funded externally before deployment. Each wallet will buy using
                      its available SOL balance (0.02 SOL reserved for fees).
                    </HelperText>
                    <HelperText>
                      Bundle buy requires a 1 SOL fee sent to the Sentry fees wallet at deployment.
                    </HelperText>
                  </>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Mass Call</Label>
                <ToggleRow>
                  <input
                    type="checkbox"
                    checked={massCallEnabled}
                    onChange={(event) => setMassCallEnabled(event.target.checked)}
                    disabled={isDeploying}
                  />
                  Send announcement + call button to all admin groups
                </ToggleRow>
                {massCallEnabled && (
                  <HelperText>
                    Mass call requires a 1 SOL fee and sends the token details + call button to every group where Sentry is admin.
                  </HelperText>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Display Username</Label>
                <ToggleRow>
                  <input
                    type="checkbox"
                    checked={displayUsername}
                    onChange={(event) => setDisplayUsername(event.target.checked)}
                    disabled={isDeploying}
                  />
                  Show my username on the token card
                </ToggleRow>
              </FormGroup>
              <FormGroup>
                <SectionHeaderRow
                  type="button"
                  $open={socialsOpen}
                  onClick={() => setSocialsOpen(prev => !prev)}
                >
                  Socials
                  <SectionChevron $open={socialsOpen}>⌄</SectionChevron>
                </SectionHeaderRow>
                {socialsOpen && (
                  <>
                    <Label>Website</Label>
                    <Input
                      type="text"
                      placeholder="https://yourproject.com"
                      value={website}
                      onChange={(event) => setWebsite(event.target.value)}
                      disabled={isDeploying}
                    />
                    <Label>X / Twitter</Label>
                    <Input
                      type="text"
                      placeholder="https://x.com/yourproject"
                      value={twitter}
                      onChange={(event) => setTwitter(event.target.value)}
                      disabled={isDeploying}
                    />
                    <Label>Telegram</Label>
                    <Input
                      type="text"
                      placeholder="https://t.me/yourproject"
                      value={telegram}
                      onChange={(event) => setTelegram(event.target.value)}
                      disabled={isDeploying}
                    />
                  </>
                )}
              </FormGroup>
              <ActionButton type="submit" disabled={!canSubmit}>
                {isDeploying ? 'Deploying…' : 'Deploy Token'}
              </ActionButton>
            </Form>
          </>
        )}
        {error && <HelperText>{error}</HelperText>}
        {logs.length > 0 && (
          <LogArea>
            {logs.join('\n')}
          </LogArea>
        )}
        {deployResult && (
          <ResultCard>
            <ResultHeader>
              {deployResult.logoUrl ? (
                <ResultLogo src={deployResult.logoUrl} alt={`${deployResult.symbol} logo`} />
              ) : null}
              <ResultTitle>{deployResult.name}</ResultTitle>
              <ResultSubtitle>{deployResult.symbol}</ResultSubtitle>
            </ResultHeader>
            <ResultRow>
              <ResultLabel>Token</ResultLabel>
              <ResultValueButton type="button" onClick={() => copyValue(deployResult.mintAddress)}>
                {deployResult.mintAddress}
              </ResultValueButton>
            </ResultRow>
            <ResultRow>
              <ResultLabel>Total Supply</ResultLabel>
              <div>1,000,000,000 {deployResult.symbol}</div>
            </ResultRow>
            <ResultRow>
              <ResultLabel>Pair</ResultLabel>
              {deployResult.poolAddress ? (
                <ResultValueButton type="button" onClick={() => copyValue(deployResult.poolAddress!)}>
                  {deployResult.poolAddress}
                </ResultValueButton>
              ) : (
                <div>Pending</div>
              )}
            </ResultRow>
            <ResultRow>
              <ResultLabel>Liquidity Lock</ResultLabel>
              {deployResult.lockSignature ? (
                <ResultLink
                  href={`https://solscan.io/tx/${deployResult.lockSignature}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View on Solscan
                </ResultLink>
              ) : (
                <div>Pending</div>
              )}
            </ResultRow>
            <ResultRow>
              <ResultLabel>Dev Buy</ResultLabel>
              {deployResult.devBuySignature ? (
                <ResultLink
                  href={`https://solscan.io/tx/${deployResult.devBuySignature}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View on Solscan
                </ResultLink>
              ) : (
                <div>Pending</div>
              )}
              {deployResult.devBuyTokenAmount !== null && deployResult.devBuyTokenAmount !== undefined && (
                <HelperText>
                  {deployResult.devBuyTokenAmount.toLocaleString()} {deployResult.symbol}
                </HelperText>
              )}
            </ResultRow>
            <ResultRow>
              <ResultLabel>Bundle Buy</ResultLabel>
              {deployResult.bundleBuyExecuted ? (
                <div>
                  Executed{deployResult.bundleBuyWalletCount ? ` (${deployResult.bundleBuyWalletCount} wallets)` : ''}
                </div>
              ) : (
                <div>Skipped</div>
              )}
            </ResultRow>
            {deployResult.massCallEnabled && (
              <ResultRow>
                <ResultLabel>Mass Call</ResultLabel>
                <div>
                  Sent: {deployResult.massCallSentCount ?? 0}
                  {deployResult.massCallFailedCount ? ` • Failed: ${deployResult.massCallFailedCount}` : ''}
                </div>
              </ResultRow>
            )}
            {deployResult.bundleBuyResults && deployResult.bundleBuyResults.length > 0 && (
              <ResultRow>
                <ResultLabel>Bundle Buys</ResultLabel>
                <div>
                  <HelperText>
                    {deployResult.bundleBuyResults.length} buys
                    {deployResult.bundleBuyTokenTotal !== null && deployResult.bundleBuyTokenTotal !== undefined
                      ? ` • Total ${deployResult.bundleBuyTokenTotal.toLocaleString(undefined, { maximumFractionDigits: 6 })} ${deployResult.symbol}`
                      : ''}
                  </HelperText>
                  <ResultLink
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      setBundleDetailsOpen(!bundleDetailsOpen);
                    }}
                  >
                    {bundleDetailsOpen ? 'Hide details' : 'Show details'}
                  </ResultLink>
                  {bundleDetailsOpen && (
                    <div style={{ maxHeight: '220px', overflowY: 'auto', marginTop: '8px' }}>
                      {deployResult.bundleBuyResults.map((buy, index) => (
                        <HelperText key={`${buy.signature}-${index}`}>
                          Wallet {index + 1} • {formatWalletAddress(buy.wallet)} • {buy.amountSol.toFixed(4)} SOL •{' '}
                          {buy.tokenAmount.toLocaleString(undefined, { maximumFractionDigits: 6 })} {deployResult.symbol} •{' '}
                          <ResultLink
                            href={`https://solscan.io/tx/${buy.signature}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View on Solscan
                          </ResultLink>
                        </HelperText>
                      ))}
                    </div>
                  )}
                </div>
              </ResultRow>
            )}
            {deployResult.bundleFeeSignature && (
              <ResultRow>
                <ResultLabel>Bundle Fee</ResultLabel>
                <ResultLink
                  href={`https://solscan.io/tx/${deployResult.bundleFeeSignature}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View on Solscan
                </ResultLink>
              </ResultRow>
            )}
            {deployResult.massCallFeeSignature && (
              <ResultRow>
                <ResultLabel>Mass Call Fee</ResultLabel>
                <ResultLink
                  href={`https://solscan.io/tx/${deployResult.massCallFeeSignature}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View on Solscan
                </ResultLink>
              </ResultRow>
            )}
            <ResultRow>
              <ResultLabel>DexScreener</ResultLabel>
              <ResultLink
                href={`https://dexscreener.com/solana/${deployResult.mintAddress}`}
                target="_blank"
                rel="noreferrer"
              >
                View on DexScreener
              </ResultLink>
            </ResultRow>
          </ResultCard>
        )}
      </Card>
      {showBundleFeeModal && (
        <ModalBackdrop onClick={() => setShowBundleFeeModal(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Deployment Fees</ModalTitle>
              <CloseButton type="button" onClick={() => setShowBundleFeeModal(false)}>×</CloseButton>
            </ModalHeader>
            <HelperText>
              This deployment includes paid features. Proceed to pay the fees to the Sentry fees wallet.
            </HelperText>
            <HelperText>
              Total fees: {(bundleBuyEnabled ? BUNDLE_FEE_SOL : 0) + (massCallEnabled ? MASS_CALL_FEE_SOL : 0)} SOL
            </HelperText>
            <ModalActionRow>
              <ModalButtonDanger
                type="button"
                onClick={() => {
                  setShowBundleFeeModal(false);
                  setPendingDevBuyAmount(undefined);
                }}
              >
                No
              </ModalButtonDanger>
              <ModalButton
                type="button"
                $secondary
                onClick={() => startDeployment(pendingDevBuyAmount)}
              >
                Yes, pay {(bundleBuyEnabled ? BUNDLE_FEE_SOL : 0) + (massCallEnabled ? MASS_CALL_FEE_SOL : 0)} SOL
              </ModalButton>
            </ModalActionRow>
          </ModalCard>
        </ModalBackdrop>
      )}
    </PageContainer>
  );
};

export default SentryCreatePWA;
